import type { PeriodSpec, ScopeFilters } from '@contracts';
import { authedFetch } from '@/lib/apiClient';
import { BRIDGE_URL as BRIDGE } from '@/lib/config';
import { resolvePeriod } from '@/lib/period';

export interface ScopeOption {
  /** Numérico (Odoo) o string `sb:branch:<nombre>` (SmartBamboo). */
  id: number | string;
  name: string;
}

export interface FilterOptions {
  regions: ScopeOption[];
  branches: ScopeOption[];
  teams: ScopeOption[];
  groups: ScopeOption[];
  employees: ScopeOption[];
}

/**
 * Opciones de los filtros de scope. La Sucursal cascadea a Equipo; y Equipo/Sucursal/Región +
 * período cascadean a Empleado y Grupo (solo los que tienen comisión en ese contexto = el
 * ranking de Vendedores). Empleado se acota además al Grupo seleccionado.
 */
export async function fetchOptions(
  companies: string[],
  scope: ScopeFilters = {},
  period?: PeriodSpec,
): Promise<FilterOptions> {
  const params = new URLSearchParams();
  if (companies.length) params.set('companies', companies.join(','));
  // Solo las sucursales Odoo (numéricas) cascadean; las de SB no.
  const numericBranches = (scope.branches ?? []).filter((b): b is number => typeof b === 'number');
  if (numericBranches.length) params.set('branches', numericBranches.join(','));
  if (scope.teams?.length) params.set('teams', scope.teams.join(','));
  if (scope.groups?.length) params.set('groups', scope.groups.join(','));
  if (scope.regions?.length) params.set('regions', scope.regions.join(','));
  if (period) {
    const { from, to } = resolvePeriod(period);
    params.set('periodFrom', from);
    params.set('periodTo', to);
  }
  const res = await authedFetch(`${BRIDGE}/metrics/options?${params.toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'No se pudieron cargar las opciones');
  return body.data as FilterOptions;
}
